import { useMemo } from 'react'
import { encodeMorse } from '@modules/MapScreen/components/MapNavaid/morse'
import { formatNavaidFrequency, NavaidFeatureInfoData } from '@modules/MapScreen/components/MapNavaid/navaidFormatters'
import { Assets, Button, Pill } from '@nexds/web'
import { useTheme } from 'styled-components'

import { UniqueEntityID } from '@/domain/core'
import { Navaid } from '@/domain/models/Waypoint/Navaid'
import { AddWaypointToRouteUseCase } from '@/domain/useCases/Route'

import {
  CloseButton,
  Divider,
  Header,
  MorseCell,
  MorseDash,
  MorseDot,
  MorseRow,
  NavaidActions,
  NavaidBody,
  NavaidPurposeRow,
  NavaidRemarksValue,
  ObstacleField,
  ObstacleFieldLabel,
  ObstacleFieldValuePrimary,
  ObstacleFieldValueText,
  Title
} from './InfoPanel.styles'

import { useI18n } from '@/presentation/hooks/useI18n'
import { openDrawer } from '@/presentation/router/DrawerRouter'
import { RouterPaths } from '@/presentation/router/RouterPathsMapper'

interface NavaidFeatureInfoProps {
  title: string
  navaid: NavaidFeatureInfoData
  onClose?: () => void
}

const EMPTY_VALUE_FALLBACK = '—'

function NavaidFeatureInfo({ title, navaid, onClose }: NavaidFeatureInfoProps) {
  const theme = useTheme()
  const { t } = useI18n()

  const frequencyLabel = useMemo(() => {
    const formatted = formatNavaidFrequency(navaid.frequency, navaid.navaidType, navaid.frequencyUom)
    return formatted || EMPTY_VALUE_FALLBACK
  }, [navaid.frequency, navaid.navaidType, navaid.frequencyUom])
  const nameLabel = navaid.name && navaid.name.length > 0 ? navaid.name : EMPTY_VALUE_FALLBACK
  const morseSequences = useMemo(() => encodeMorse(navaid.code), [navaid.code])

  const purposeLabel =
    navaid.purpose === 'ENROUTE'
      ? t('MAP_NAVAID_INFO_PURPOSE-ENROUTE')
      : navaid.purpose === 'TERMINAL'
        ? t('MAP_NAVAID_INFO_PURPOSE-TERMINAL')
        : undefined

  const handleAddToRoute = () => {
    // O painel só recebe os dados (NavaidFeatureInfoData); reconstruímos a instância
    // de Navaid para alimentar o use case, preservando o id original.
    const navaidModel = Navaid.create(
      {
        coordinates: navaid.coordinates,
        code: navaid.code,
        name: navaid.name
      },
      {
        type: navaid.navaidType,
        frequency: navaid.frequency,
        frequencyUom: navaid.frequencyUom,
        purpose: navaid.purpose,
        operationalHours: navaid.operationalHours,
        remarks: navaid.remarks
      },
      new UniqueEntityID(navaid.id)
    )

    const updatedRoute = new AddWaypointToRouteUseCase().execute(navaidModel)
    if (updatedRoute.isFailure) return

    // Mesmo comportamento da busca/WaypointInfo: abre o painel de rotas no primeiro ponto.
    if (updatedRoute.getValue().waypoints.length === 1) {
      openDrawer(RouterPaths.ROUTES_PANEL)
    }
  }

  return (
    <>
      <Header>
        <Title>{title}</Title>
        <CloseButton onClick={onClose}>
          <Assets.Close size={'md'} color={theme.colors.secondaryL3} />
        </CloseButton>
      </Header>
      <NavaidBody>
        {purposeLabel && (
          <NavaidPurposeRow>
            <Pill
              label={purposeLabel.toUpperCase()}
              size="xs"
              textSize="xs"
              variant="outline"
              color={theme.colors.primary}
              textFittedWidth
            />
          </NavaidPurposeRow>
        )}

        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_NAVAID_INFO_FREQUENCY-LABEL')}</ObstacleFieldLabel>
          <ObstacleFieldValuePrimary>{frequencyLabel}</ObstacleFieldValuePrimary>
        </ObstacleField>

        <Divider />
        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_NAVAID_INFO_MORSE-LABEL')}</ObstacleFieldLabel>
          {morseSequences.length > 0 ? (
            <MorseRow role="img" aria-label={navaid.code}>
              {morseSequences.map((sequence, cellIndex) => (
                <MorseCell key={`${sequence}-${cellIndex}`}>
                  {sequence
                    .split('')
                    .map((symbol, symbolIndex) =>
                      symbol === '-' ? (
                        <MorseDash key={`dash-${symbolIndex}`} />
                      ) : (
                        <MorseDot key={`dot-${symbolIndex}`} />
                      )
                    )}
                </MorseCell>
              ))}
            </MorseRow>
          ) : (
            <ObstacleFieldValueText>{EMPTY_VALUE_FALLBACK}</ObstacleFieldValueText>
          )}
        </ObstacleField>

        <Divider />
        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_NAVAID_INFO_NAME-LABEL')}</ObstacleFieldLabel>
          <ObstacleFieldValueText>{nameLabel}</ObstacleFieldValueText>
        </ObstacleField>

        <Divider />
        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_NAVAID_INFO_OPERATION-LABEL')}</ObstacleFieldLabel>
          <ObstacleFieldValueText>{navaid.operationalHours || EMPTY_VALUE_FALLBACK}</ObstacleFieldValueText>
        </ObstacleField>

        <Divider />
        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_NAVAID_INFO_OBSERVATIONS-LABEL')}</ObstacleFieldLabel>
          {navaid.remarks ? (
            <NavaidRemarksValue>{navaid.remarks}</NavaidRemarksValue>
          ) : (
            <ObstacleFieldValueText>{EMPTY_VALUE_FALLBACK}</ObstacleFieldValueText>
          )}
        </ObstacleField>

        <NavaidActions>
          <Button
            label={t('MAP_NAVAID_INFO_ADD-TO-ROUTE')}
            variant="outline"
            color="secondary"
            size="sm"
            leftIcon="Plus"
            fullWidth
            onPress={handleAddToRoute}
          />
        </NavaidActions>
      </NavaidBody>
    </>
  )
}

export { NavaidFeatureInfo }
